import React from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';

const ButtonWrapper = styled(motion.button).withConfig({
  shouldForwardProp: (prop) => !['variant', 'size', 'fullWidth', 'loading'].includes(prop)
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  border: none;
  border-radius: var(--radius-lg);
  font-family: var(--font-family-primary);
  font-weight: var(--font-weight-medium);
  text-decoration: none;
  cursor: pointer;
  transition: all var(--transition-fast);
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  &:focus {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }

  /* Size variants */
  ${props => props.size === 'sm' && css`
    padding: var(--spacing-sm) var(--spacing-md);
    font-size: var(--font-size-sm);
    min-height: 32px;
  `}
  
  ${props => props.size === 'md' && css`
    padding: var(--spacing-md) var(--spacing-lg);
    font-size: var(--font-size-base);
    min-height: 40px;
  `}
  
  ${props => props.size === 'lg' && css`
    padding: var(--spacing-lg) var(--spacing-2xl);
    font-size: var(--font-size-lg);
    min-height: 48px;
  `}
  
  ${props => props.size === 'xl' && css`
    padding: var(--spacing-xl) var(--spacing-3xl);
    font-size: var(--font-size-xl);
    min-height: 56px;
  `}

  /* Variant styles */
  ${props => props.variant === 'primary' && css`
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
    color: var(--color-white);
    box-shadow: var(--shadow-sm);
    
    &:hover:not(:disabled) {
      background: linear-gradient(135deg, var(--color-primary-dark) 0%, var(--color-primary) 100%);
      box-shadow: var(--shadow-md);
      transform: translateY(-1px);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: var(--shadow-sm);
    }
  `}
  
  ${props => props.variant === 'secondary' && css`
    background: var(--color-white);
    color: var(--color-primary);
    border: 1px solid var(--color-border);
    box-shadow: var(--shadow-sm);
    
    &:hover:not(:disabled) {
      background: var(--color-gray-50);
      border-color: var(--color-primary);
      box-shadow: var(--shadow-md);
      transform: translateY(-1px);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: var(--shadow-sm);
    }
  `}
  
  ${props => props.variant === 'outline' && css`
    background: transparent;
    color: var(--color-primary);
    border: 1px solid var(--color-primary);
    
    &:hover:not(:disabled) {
      background: var(--color-primary);
      color: var(--color-white);
      transform: translateY(-1px);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
  `}
  
  ${props => props.variant === 'ghost' && css`
    background: transparent;
    color: var(--color-text);
    
    &:hover:not(:disabled) {
      background: var(--color-gray-100);
      transform: translateY(-1px);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
  `}
  
  ${props => props.variant === 'danger' && css`
    background: linear-gradient(135deg, var(--color-error) 0%, #D70015 100%);
    color: var(--color-white);
    box-shadow: var(--shadow-sm);
    
    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #D70015 0%, var(--color-error) 100%);
      box-shadow: var(--shadow-md);
      transform: translateY(-1px);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: var(--shadow-sm);
    }
  `}
  
  ${props => props.variant === 'success' && css`
    background: linear-gradient(135deg, var(--color-success) 0%, #28A745 100%);
    color: var(--color-white);
    box-shadow: var(--shadow-sm);
    
    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #28A745 0%, var(--color-success) 100%);
      box-shadow: var(--shadow-md);
      transform: translateY(-1px);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: var(--shadow-sm);
    }
  `}

  /* Full width */
  ${props => props.fullWidth && css`
    width: 100%;
  `}

  /* Loading state */
  ${props => props.loading && css`
    cursor: wait;
    
    &:hover:not(:disabled) {
      transform: none;
    }
  `}
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Button = React.forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled = false,
  leftIcon,
  rightIcon,
  onClick,
  type = 'button',
  ...props
}, ref) => {
  const handleClick = (e) => {
    if (loading || disabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  // Destructure DOM-invalid props before spreading
  const {
    // Remove any other custom props that might be passed
    ...domProps
  } = props;

  return (
    <ButtonWrapper
      ref={ref}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      loading={loading}
      disabled={disabled || loading}
      onClick={handleClick}
      type={type}
      whileTap={{ scale: disabled || loading ? 1 : 0.98 }}
      {...domProps}
    >
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {leftIcon && <span>{leftIcon}</span>}
          {children}
          {rightIcon && <span>{rightIcon}</span>}
        </>
      )}
    </ButtonWrapper>
  );
});

Button.displayName = 'Button';

export default Button; 